import React from 'react';
import { Avatar } from 'primereact/avatar';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Chip } from 'primereact/chip';

/**
 * Profile hero block — cover banner with overlapping circular avatar,
 * name + headline + meta strip, and a trailing actions slot.
 *
 * Promoted from the prototype with extensions for production photo upload:
 *   • onAvatarClick / onCoverClick — wire to file input click
 *   • uploadingPhoto                — shows spinner over the camera icon
 *   • dataSourceTag                  — optional Chip slot for "LinkedIn synced" etc.
 *   • lendingNetworkPending          — show a warning Tag if access is pending
 */
const HeroIdentity = ({
    profile,
    fallbackInitials,
    fallbackColor = 'blue',
    actions,
    onAvatarClick,
    onCoverClick,
    onCoverDelete,
    onAvatarDelete,
    uploadingPhoto = false,
    uploadingCover = false,
    dataSourceTag,
    lendingNetworkPending = false,
    // Optional overrides for the connection count meta line. When set, the
    // hero shows "<bold>{connectionValue}</bold> {connectionLabel}" instead
    // of the default "<connection_count> connections". Used by ViewAgentProfile
    // to render bucketed mutual counts ("10+ mutual connections").
    connectionLabel,
    connectionValue,
    // When set, the connection meta line renders as a clickable link in the
    // primary color and on its own row of the meta strip (so it stands out as
    // a navigation target — opens the mutual connections dialog).
    onConnectionClick,
}) => {
    if (!profile) return null;

    const initials = fallbackInitials
        || (profile.full_name?.split(' ').map((p) => p[0]).slice(0, 2).join('') || 'U').toUpperCase();

    const coverBg = profile.cover_photo_url_full
        ? `url(${profile.cover_photo_url_full}) center/cover`
        : `linear-gradient(135deg, var(--${fallbackColor}-400), var(--${fallbackColor}-600))`;

    const location = [profile.location_city, profile.location_state]
        .filter(Boolean)
        .join(', ');

    return (
        <div className="surface-card shadow-1 border-round-xl overflow-hidden mb-3">
            {/* Cover banner */}
            <div className="relative" style={{ height: '6rem', background: coverBg }}>
                {/* Loading overlay while a cover upload / delete is in flight */}
                {uploadingCover && (
                    <div
                        className="absolute top-0 left-0 w-full h-full flex align-items-center justify-content-center"
                        style={{ backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 5 }}
                    >
                        <i className="pi pi-spin pi-spinner text-white text-xl" aria-hidden="true" />
                    </div>
                )}
                {(onCoverClick || onCoverDelete) && (
                    <div
                        className="absolute flex gap-1"
                        style={{ top: '0.5rem', right: '0.5rem' }}
                    >
                        {onCoverClick && (
                            <Button
                                icon="pi pi-camera"
                                rounded
                                size="small"
                                severity="secondary"
                                onClick={onCoverClick}
                                disabled={uploadingCover}
                                tooltip={profile.cover_photo_url_full ? 'Change cover' : 'Add cover'}
                                tooltipOptions={{ position: 'left' }}
                                aria-label="Edit cover image"
                                pt={{
                                    root: {
                                        style: {
                                            backgroundColor: 'rgba(255,255,255,0.95)',
                                            color: 'var(--text-color)',
                                            border: 'none',
                                            backdropFilter: 'blur(4px)',
                                            width: '2rem',
                                            height: '2rem',
                                            padding: 0,
                                            minWidth: '2rem',
                                        },
                                    },
                                }}
                            />
                        )}
                        {onCoverDelete && profile.cover_photo_url_full && (
                            <Button
                                icon="pi pi-trash"
                                rounded
                                size="small"
                                severity="danger"
                                onClick={onCoverDelete}
                                disabled={uploadingCover}
                                tooltip="Remove cover"
                                tooltipOptions={{ position: 'left' }}
                                aria-label="Remove cover image"
                                pt={{
                                    root: {
                                        style: {
                                            backgroundColor: 'rgba(220, 38, 38, 0.9)',
                                            border: 'none',
                                            width: '2rem',
                                            height: '2rem',
                                            padding: 0,
                                            minWidth: '2rem',
                                        },
                                    },
                                }}
                            />
                        )}
                    </div>
                )}
            </div>

            {/* Body — identity + actions on white, below the cover */}
            <div className="p-3 md:p-4">
                {/* Row 1: Avatar (overlaps cover) + actions */}
                <div
                    className="flex align-items-end justify-content-between gap-3 mb-3"
                    style={{ marginTop: '-2.75rem' }}
                >
                    <div className="relative" style={{ width: 'fit-content' }}>
                        <Avatar
                            label={initials}
                            image={profile.profile_photo_url_full}
                            shape="circle"
                            style={{
                                width: '5.5rem',
                                height: '5.5rem',
                                fontSize: '1.5rem',
                                fontWeight: 700,
                                backgroundColor: `var(--${fallbackColor}-100)`,
                                color: `var(--${fallbackColor}-700)`,
                                border: '3px solid var(--surface-0)',
                            }}
                        />
                        {onAvatarClick && (
                            <Button
                                icon={uploadingPhoto ? 'pi pi-spin pi-spinner' : 'pi pi-camera'}
                                rounded
                                size="small"
                                onClick={onAvatarClick}
                                disabled={uploadingPhoto}
                                aria-label="Update profile photo"
                                pt={{
                                    root: {
                                        style: {
                                            position: 'absolute',
                                            bottom: 0,
                                            right: 0,
                                            width: '1.75rem',
                                            height: '1.75rem',
                                            padding: 0,
                                            minWidth: '1.75rem',
                                            borderRadius: '50%',
                                            border: '2px solid var(--surface-0)',
                                        },
                                    },
                                }}
                            />
                        )}
                        {onAvatarDelete && profile.profile_photo_url_full && (
                            <Button
                                icon="pi pi-trash"
                                rounded
                                size="small"
                                severity="danger"
                                onClick={onAvatarDelete}
                                aria-label="Delete profile photo"
                                pt={{
                                    root: {
                                        style: {
                                            position: 'absolute',
                                            bottom: 0,
                                            left: 0,
                                            width: '1.5rem',
                                            height: '1.5rem',
                                            padding: 0,
                                            minWidth: '1.5rem',
                                            borderRadius: '50%',
                                            border: '2px solid var(--surface-0)',
                                        },
                                    },
                                }}
                            />
                        )}
                    </div>

                    {actions && (
                        <div className="flex gap-1 align-self-end">{actions}</div>
                    )}
                </div>

                {/* Row 2: Name */}
                <h1 className="text-900 font-bold m-0 mb-1 text-lg md:text-xl">
                    {profile.full_name || 'Unnamed profile'}
                </h1>

                {/* Row 3: Headline */}
                {profile.headline && (
                    <p className="text-700 text-sm m-0 mb-2">{profile.headline}</p>
                )}

                {/* Row 4: Meta strip */}
                <div className="flex flex-column md:flex-row md:flex-wrap md:gap-x-3 gap-y-1 text-xs text-600 mb-2">
                    {profile.current_company && (
                        <div className="flex align-items-center gap-2">
                            <i
                                className="pi pi-briefcase flex-shrink-0"
                                style={{ fontSize: '0.75rem', width: '1rem' }}
                                aria-hidden="true"
                            />
                            <span>
                                {profile.current_designation
                                    ? `${profile.current_designation} · ${profile.current_company}`
                                    : profile.current_company}
                            </span>
                        </div>
                    )}
                    {location && (
                        <div className="flex align-items-center gap-2">
                            <i
                                className="pi pi-map-marker flex-shrink-0"
                                style={{ fontSize: '0.75rem', width: '1rem' }}
                                aria-hidden="true"
                            />
                            <span>{location}</span>
                        </div>
                    )}
                    {Number(profile.experience_years) > 0 && (
                        <div className="flex align-items-center gap-2">
                            <i
                                className="pi pi-clock flex-shrink-0"
                                style={{ fontSize: '0.75rem', width: '1rem' }}
                                aria-hidden="true"
                            />
                            <span>
                                {Number(profile.experience_years) % 1 !== 0
                                    ? `${parseFloat(profile.experience_years)} years experience`
                                    : `${parseInt(profile.experience_years)}+ years experience`}
                            </span>
                        </div>
                    )}
                    <div
                        className={`flex align-items-center gap-2${
                            onConnectionClick ? ' w-full md:flex-basis-full' : ''
                        }`}
                        style={onConnectionClick ? { flexBasis: '100%' } : undefined}
                    >
                        <i
                            className="pi pi-users flex-shrink-0"
                            style={{
                                fontSize: '0.75rem',
                                width: '1rem',
                                color: onConnectionClick ? 'var(--primary-color)' : undefined,
                            }}
                            aria-hidden="true"
                        />
                        {onConnectionClick ? (
                            <button
                                type="button"
                                onClick={onConnectionClick}
                                className="bg-transparent border-none p-0 cursor-pointer text-primary font-medium hover:underline"
                                style={{ font: 'inherit' }}
                            >
                                <span className="font-semibold">
                                    {connectionValue ?? 0}
                                </span>{' '}
                                {connectionLabel ?? 'connections'}
                            </button>
                        ) : (
                            <span>
                                <span className="font-semibold text-900">
                                    {connectionValue ?? (profile.connection_count || 0)}
                                </span>{' '}
                                {connectionLabel ?? 'connections'}
                            </span>
                        )}
                    </div>
                </div>

                {/* Row 5: Optional badges (data source, LN gate) */}
                {(dataSourceTag || lendingNetworkPending) && (
                    <div className="flex align-items-center gap-2 flex-wrap">
                        {dataSourceTag}
                        {lendingNetworkPending && (
                            <Tag
                                value="Lending Network — access pending"
                                icon="pi pi-clock"
                                severity="warning"
                                className="text-xs"
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default HeroIdentity;
